import { createAsyncThunk, createEntityAdapter, createSlice } from '@reduxjs/toolkit';

import { del, get, post, put } from 'data/services/config';
import { isPendingAction, isRejectedAction } from './common';
import { Phase } from './phaseTypes';

// Adapter for operate and normalize the data
export const phasesAdapter = createEntityAdapter<Phase>();

const initialState = phasesAdapter.getInitialState({
  loading: false,
  deleting: false,
  saving: false,
});

// Actions
export const fetchPhases = createAsyncThunk(
  'phases/fetch',
  async (eventId: string, { rejectWithValue }) => {
    try {
      return await get(`/events/${eventId}/phases`);
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const deletePhase = createAsyncThunk(
  'phases/delete',
  async ({ eventId, phaseId }: { eventId: string; phaseId: string }, { rejectWithValue }) => {
    try {
      await del(`/events/${eventId}/phases/${phaseId}`, {});
      return { phaseId };
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const savePhase = createAsyncThunk(
  'phases/save',
  async ({ eventId, phase }: { eventId: string; phase: Phase }, { rejectWithValue }) => {
    try {
      return await post(`/events/${eventId}/phases`, { phase });
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const updatePhase = createAsyncThunk(
  'phases/update',
  async ({ phaseId, phase }: { phaseId: string; phase: Phase }, { rejectWithValue }) => {
    try {
      return await put(`/phases/${phaseId}`, { phase });
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const phasesSlice = createSlice({
  name: 'phasesSlice',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchPhases.fulfilled, (state, action) => {
        phasesAdapter.upsertMany(state, action.payload.phases);
        state.loading = false;
      })
      .addCase(deletePhase.fulfilled, (state, action) => {
        phasesAdapter.removeOne(state, action.payload.phaseId);
        state.deleting = false;
        state.loading = false;
      })
      .addCase(deletePhase.rejected, (state) => {
        state.deleting = false;
        state.loading = false;
      })
      .addMatcher(
        (action) => [savePhase.pending.type, updatePhase.pending.type].includes(action.type),
        (state) => {
          state.saving = true;
        },
      )
      .addMatcher(
        (action) => [savePhase.fulfilled.type, updatePhase.fulfilled.type].includes(action.type),
        (state, action) => {
          const payload = (action as any).payload;
          const phase = payload?.phase ?? payload;
          phasesAdapter.upsertOne(state, phase);
          state.saving = false;
          state.loading = false;
        },
      )
      .addMatcher(
        (action) => [savePhase.rejected.type, updatePhase.rejected.type].includes(action.type),
        (state) => {
          state.saving = false;
          state.loading = false;
        },
      )
      .addMatcher(isPendingAction('phases'), (state) => {
        state.loading = true;
      })
      .addMatcher(isRejectedAction('phases'), (state) => {
        state.loading = false;
      });
  },
  selectors: {
    ...phasesAdapter.getSelectors(),
    getIsLoading: (state) => state.loading,
    getIsDeleting: (state) => state.deleting,
    getIsSaving: (state) => state.saving,
    getPhasesByEventId: (state, eventId: string) => {
      const phases = phasesAdapter.getSelectors().selectEntities(state);
      return Object.values(phases).filter((phase: Phase) => phase.event_id === eventId);
    },
  },
});

export default phasesSlice.reducer;
export const fromPhases = phasesSlice.selectors;
